import { IQuoteOfferingRespSchema } from '@monetize/types/app';
import { toDateShort } from '@monetize/utils/core';
import { MdInfo } from 'react-icons/md';
import {
  MBox,
  MFlex,
  MIcon,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';

export interface QuoteOfferingDateProps {
  isChildOffering: boolean;
  /** Amendment or contract start date - used to determine if startDate was modified */
  quoteStartDate?: string;
  /**
   * How the tooltip names `quoteStartDate`. On an amendment that date is the
   * amendment date, and every carried-over offering starts before it.
   */
  quoteStartDateLabel?: string;
  quoteEndDate?: string;
  startDate?: string;
  endDate?: string;
  isLastSegment?: boolean;
  isOfferingOneTime?: boolean;
  isOpen: boolean;
  childOfferingCount?: number;
  childQuoteOfferings: IQuoteOfferingRespSchema[];
  offeringEndDate: string;
  isOfferingEndBeforeContract: boolean;
}

export const QuoteOfferingDate = ({
  isChildOffering,
  quoteStartDate,
  quoteStartDateLabel = 'quote start date',
  quoteEndDate,
  startDate,
  endDate,
  isOpen,
  isLastSegment = false,
  childOfferingCount = 0,

  isOfferingOneTime = false,
  childQuoteOfferings = [],
  offeringEndDate,
  isOfferingEndBeforeContract,
}: QuoteOfferingDateProps) => {
  if (!quoteStartDate || !startDate || !endDate) {
    return <MBox />;
  }

  // A start date that differs from the quote's own is emphasized and explained
  // in a tooltip. It used to be underlined too, which read as a link.
  const isStartDateModified = !isChildOffering && startDate !== quoteStartDate;

  const startDateText = (
    <MText fontWeight={isStartDateModified ? '600' : '400'}>
      {toDateShort(startDate)}
    </MText>
  );

  return (
    <MFlex
      alignItems="center"
      whiteSpace="pre"
      fontSize="sm"
      fontWeight="normal"
      color="tPurple.dark"
      minH="8"
    >
      {isStartDateModified ? (
        <MTooltip
          label={`Differs from the ${quoteStartDateLabel} (${toDateShort(quoteStartDate)})`}
          placement="bottom-start"
          shouldWrapChildren
        >
          {startDateText}
        </MTooltip>
      ) : (
        startDateText
      )}
      {!isOfferingOneTime && (
        <>
          <MBox as="span" mx={1}>
            –
          </MBox>
          {toDateShort(offeringEndDate)}
          {isOfferingEndBeforeContract && (
            <MTooltip
              label="Offering ends before end of Contract"
              placement="bottom-start"
              size="md"
            >
              <MFlex alignItems="center" ml="2">
                <MIcon as={MdInfo} color="tOrange.tangerine" />
              </MFlex>
            </MTooltip>
          )}
        </>
      )}
    </MFlex>
  );
};

export default QuoteOfferingDate;
